import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Grid,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { Colors } from "utils/Constants";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

const piePercentagePlugin = {
  id: "piePercentagePlugin",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const dataset = chart.data.datasets[0];
    const total = dataset.data.reduce((sum, value) => sum + value, 0);
    if (!total) return;
    ctx.save();
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "700 12px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    chart.getDatasetMeta(0).data.forEach((arc, index) => {
      const percentage = `${((dataset.data[index] / total) * 100).toFixed(1)}%`;
      const position = arc.tooltipPosition();
      ctx.fillText(percentage, position.x, position.y);
    });
    ctx.restore();
  },
};

const loanPresets = {
  home: { label: "Home Loan", amount: 5000000, rate: 8.5, years: 20 },
  personal: { label: "Personal Loan", amount: 800000, rate: 12, years: 5 },
  car: { label: "Car Loan", amount: 1000000, rate: 9.5, years: 7 },
};

const formatCurrency = (value) => currencyFormatter.format(Number(value) || 0);

const getChartImage = (chartRef, canvasId) => {
  chartRef.current?.update?.("none");
  const chartImage = chartRef.current?.toBase64Image?.("image/png", 1);
  if (chartImage) return chartImage;

  return document.getElementById(canvasId)?.toDataURL?.("image/png") || "";
};

const getMonthLabel = (date) => date.toLocaleString("en-IN", { month: "short", year: "numeric" });

const getFinancialYear = (date) => {
  const year = date.getFullYear();
  return date.getMonth() >= 3
    ? `${year}-${String(year + 1).slice(2)}`
    : `${year - 1}-${String(year).slice(2)}`;
};

const calculateEmi = (principal, annualRate, months) => {
  if (!principal || principal <= 0 || !months || months <= 0) return 0;
  const monthlyRate = annualRate / 12 / 100;
  if (!monthlyRate) return principal / months;
  return (
    (principal * monthlyRate * (1 + monthlyRate) ** months) / ((1 + monthlyRate) ** months - 1)
  );
};

const buildSchedule = ({ amount, annualRate, months, startDate }) => {
  const emi = calculateEmi(amount, annualRate, months);
  let balance = amount;

  return Array.from({ length: months }, (_, index) => {
    const paymentDate = new Date(startDate.getFullYear(), startDate.getMonth() + index, 1);
    const interest = balance * (annualRate / 12 / 100);
    const principal = Math.min(emi - interest, balance);
    const closingBalance = Math.max(balance - principal, 0);
    const row = {
      monthNo: index + 1,
      month: getMonthLabel(paymentDate),
      calendarYear: paymentDate.getFullYear(),
      financialYear: getFinancialYear(paymentDate),
      emi,
      principal,
      interest,
      totalPayment: principal + interest,
      openingBalance: balance,
      closingBalance,
    };
    balance = closingBalance;
    return row;
  });
};

const groupSchedule = (schedule, groupBy) => {
  const grouped = schedule.reduce((acc, row) => {
    const key = groupBy === "financial" ? row.financialYear : row.calendarYear;
    const current = acc[key] || {
      key,
      principal: 0,
      interest: 0,
      totalPayment: 0,
      openingBalance: row.openingBalance,
      closingBalance: row.closingBalance,
      months: [],
    };

    return {
      ...acc,
      [key]: {
        ...current,
        key,
        principal: current.principal + row.principal,
        interest: current.interest + row.interest,
        totalPayment: current.totalPayment + row.totalPayment,
        closingBalance: row.closingBalance,
        months: [...current.months, row],
      },
    };
  }, {});

  return Object.values(grouped);
};

function EMICalculator() {
  const [loanType, setLoanType] = useState("home");
  const [loanAmount, setLoanAmount] = useState(loanPresets.home.amount);
  const [interestRate, setInterestRate] = useState(loanPresets.home.rate);
  const [tenureYears, setTenureYears] = useState(loanPresets.home.years);
  const [tenureMonths, setTenureMonths] = useState(0);
  const [groupBy, setGroupBy] = useState("calendar");
  const [expandedRows, setExpandedRows] = useState({});
  const [startMonth, setStartMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const pieChartRef = useRef(null);
  const barChartRef = useRef(null);

  const totalMonths = Math.max(1, Number(tenureYears) * 12 + Number(tenureMonths));
  const startDate = useMemo(() => {
    const [year, month] = startMonth.split("-").map(Number);
    return new Date(year, month - 1, 1);
  }, [startMonth]);

  const schedule = useMemo(
    () =>
      buildSchedule({
        amount: Number(loanAmount) || 0,
        annualRate: Number(interestRate) || 0,
        months: totalMonths,
        startDate,
      }),
    [loanAmount, interestRate, totalMonths, startDate]
  );

  const groupedSchedule = useMemo(() => groupSchedule(schedule, groupBy), [schedule, groupBy]);
  const emi = schedule[0]?.emi || 0;
  const totalInterest = schedule.reduce((sum, row) => sum + row.interest, 0);
  const totalPayment = (Number(loanAmount) || 0) + totalInterest;

  const handleLoanTypeChange = (nextType) => {
    const preset = loanPresets[nextType];
    setLoanType(nextType);
    setLoanAmount(preset.amount);
    setInterestRate(preset.rate);
    setTenureYears(preset.years);
    setTenureMonths(0);
  };

  const pieData = {
    labels: ["Principal Loan Amount", "Total Interest"],
    datasets: [
      {
        data: [Number(loanAmount) || 0, totalInterest],
        backgroundColor: [Colors.PRIMARY || "#043b9f", Colors.SECONDARY || "#FFC107"],
        borderColor: "#FFFFFF",
        borderWidth: 6,
        hoverOffset: 14,
      },
    ],
  };

  const barData = {
    labels: groupedSchedule.map((row) => row.key),
    datasets: [
      {
        label: "Principal",
        data: groupedSchedule.map((row) => Math.round(row.principal)),
        backgroundColor: Colors.PRIMARY || "#043b9f",
      },
      {
        label: "Interest",
        data: groupedSchedule.map((row) => Math.round(row.interest)),
        backgroundColor: Colors.SECONDARY || "#FFC107",
      },
    ],
  };

  const downloadExcel = () => {
    const headers = [
      "Month",
      "Opening Balance",
      "EMI",
      "Principal",
      "Interest",
      "Total Payment",
      "Closing Balance",
    ];
    const rows = schedule.map((row) => [
      row.month,
      Math.round(row.openingBalance),
      Math.round(row.emi),
      Math.round(row.principal),
      Math.round(row.interest),
      Math.round(row.totalPayment),
      Math.round(row.closingBalance),
    ]);
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "emi-repayment-schedule.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = () => {
    const pieChartImage = getChartImage(pieChartRef, "emi-pie-chart");
    const barChartImage = getChartImage(barChartRef, "emi-bar-chart");
    const tableRows = groupedSchedule
      .map(
        (row) => `
          <tr>
            <td>${row.key}</td>
            <td>${formatCurrency(row.principal)}</td>
            <td>${formatCurrency(row.interest)}</td>
            <td>${formatCurrency(row.totalPayment)}</td>
            <td>${formatCurrency(row.closingBalance)}</td>
          </tr>`
      )
      .join("");

    const report = window.open("", "_blank");
    report.document.write(`
      <html>
        <head>
          <title>EMI Repayment Schedule</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #101828; }
            h1 { margin-bottom: 8px; }
            .summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 20px 0; }
            .card { border: 1px solid #EAECF0; padding: 12px; border-radius: 6px; }
            .label { color: #667085; font-size: 12px; }
            .value { font-size: 18px; font-weight: 700; margin-top: 6px; }
            table { border-collapse: collapse; width: 100%; margin-top: 16px; font-size: 12px; }
            th, td { border: 1px solid #EAECF0; padding: 8px; text-align: right; }
            th:first-child, td:first-child { text-align: left; }
            th { background: #F2F4F7; }
            .charts { display: grid; grid-template-columns: 1fr; gap: 20px; margin: 20px 0; }
            .chart-card { border: 1px solid #EAECF0; border-radius: 6px; padding: 12px; page-break-inside: avoid; }
            .chart-card h2 { font-size: 16px; margin: 0 0 12px; }
            .chart-card img { display: block; max-width: 100%; margin: 0 auto; }
          </style>
        </head>
        <body>
          <h1>EMI Repayment Schedule</h1>
          <p>${loanPresets[loanType].label} | ${formatCurrency(
      loanAmount
    )} | ${interestRate}% p.a. | ${totalMonths} months</p>
          <div class="summary">
            <div class="card"><div class="label">Loan EMI</div><div class="value">${formatCurrency(
              emi
            )}</div></div>
            <div class="card"><div class="label">Total Interest</div><div class="value">${formatCurrency(
              totalInterest
            )}</div></div>
            <div class="card"><div class="label">Total Payment</div><div class="value">${formatCurrency(
              totalPayment
            )}</div></div>
          </div>
          <div class="charts">
            ${
              pieChartImage
                ? `<div class="chart-card"><h2>Break-up of Total Payment</h2><img src="${pieChartImage}" style="max-height: 320px;" /></div>`
                : ""
            }
            ${
              barChartImage
                ? `<div class="chart-card"><h2>Principal and Interest by Period</h2><img src="${barChartImage}" /></div>`
                : ""
            }
          </div>
          <table>
            <thead><tr><th>Period</th><th>Principal</th><th>Interest</th><th>Total Payment</th><th>Balance</th></tr></thead>
            <tbody>${tableRows}</tbody>
          </table>
          <script>
            const images = Array.from(document.images);
            const waitForImages = Promise.all(images.map((image) => {
              if (image.complete) return Promise.resolve();
              return new Promise((resolve) => {
                image.onload = resolve;
                image.onerror = resolve;
              });
            }));
            waitForImages.then(() => {
              setTimeout(() => {
                window.focus();
                window.print();
              }, 250);
            });
          </script>
        </body>
      </html>
    `);
    report.document.close();
  };

  return (
    <DashboardLayout>
      <Box sx={{ backgroundColor: "#F4F6FB", minHeight: "100vh", p: { xs: 1.5, md: 3 } }}>
        <Box sx={{ maxWidth: 800, mx: "auto", width: "100%" }}>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: "#101828", mb: 2 }}>
            EMI Calculator
          </Typography>

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2, boxShadow: "none", border: "1px solid #EAECF0" }}>
                <CardContent>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    value={loanType}
                    onChange={(event, value) => value && handleLoanTypeChange(value)}
                    sx={{
                      mb: 3,
                      border: "1px solid #EAECF0",
                      borderRadius: "12px",
                      p: "4px",
                      backgroundColor: "#F9FAFB",
                      "& .MuiToggleButtonGroup-grouped": {
                        border: "none",
                        borderRadius: "8px !important",
                        mx: "2px",
                        fontSize: 13,
                        fontWeight: 600,
                        textTransform: "none",
                        color: "#475467",
                        transition: "all 0.2s ease",
                        "&.Mui-selected": {
                          backgroundColor: Colors.PRIMARY || "#043b9f",
                          color: "#FFFFFF",
                          boxShadow:
                            "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)",
                          "&:hover": {
                            backgroundColor: Colors.PRIMARY || "#043b9f",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "#F2F4F7",
                        },
                      },
                    }}
                  >
                    {Object.entries(loanPresets).map(([key, preset]) => (
                      <ToggleButton key={key} value={key} disableRipple>
                        {preset.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>

                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#344054" }}>
                        Loan Amount
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Enter amount"
                        value={loanAmount === "" || loanAmount === 0 ? "" : loanAmount}
                        onChange={(event) => {
                          const val = event.target.value;
                          if (val === "" || val === null) {
                            setLoanAmount("");
                          } else {
                            const parsed = Number(val);
                            setLoanAmount(Number.isNaN(parsed) ? "" : parsed);
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ fontSize: 14, color: "#667085", mr: 0.5 }}>
                              ₹
                            </Typography>
                          ),
                        }}
                        sx={{
                          width: 140,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            fontSize: 14,
                            fontWeight: 600,
                          },
                        }}
                      />
                    </Box>
                    <Slider
                      value={Number(loanAmount) || 0}
                      min={0}
                      max={10000000}
                      step={50000}
                      onChange={(event, value) => setLoanAmount(value || "")}
                      sx={{
                        color: Colors.SECONDARY || "#FFC107",
                        height: 6,
                        "& .MuiSlider-track": { border: "none" },
                        "& .MuiSlider-thumb": {
                          height: 18,
                          width: 18,
                          backgroundColor: "#FFFFFF",
                          border: `2px solid ${Colors.SECONDARY || "#FFC107"}`,
                          "&:hover, &.Mui-active, &.Mui-focusVisible": {
                            boxShadow: `0px 0px 0px 8px rgba(255, 193, 7, 0.16)`,
                          },
                          "&::before": { display: "none" },
                        },
                        "& .MuiSlider-rail": { opacity: 0.3, backgroundColor: "#D0D5DD" },
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#344054" }}>
                        Interest Rate (% p.a.)
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        placeholder="0"
                        value={interestRate === "" || interestRate === 0 ? "" : interestRate}
                        onChange={(event) => {
                          const val = event.target.value;
                          if (val === "" || val === null) {
                            setInterestRate("");
                          } else {
                            const parsed = Number(val);
                            setInterestRate(Number.isNaN(parsed) ? "" : parsed);
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <Typography sx={{ fontSize: 14, color: "#667085", ml: 0.5 }}>
                              %
                            </Typography>
                          ),
                        }}
                        sx={{
                          width: 100,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            fontSize: 14,
                            fontWeight: 600,
                          },
                        }}
                      />
                    </Box>
                    <Slider
                      value={Number(interestRate) || 0}
                      min={0}
                      max={30}
                      step={0.05}
                      onChange={(event, value) => setInterestRate(value || "")}
                      sx={{
                        color: Colors.SECONDARY || "#FFC107",
                        height: 6,
                        "& .MuiSlider-track": { border: "none" },
                        "& .MuiSlider-thumb": {
                          height: 18,
                          width: 18,
                          backgroundColor: "#FFFFFF",
                          border: `2px solid ${Colors.SECONDARY || "#FFC107"}`,
                          "&:hover, &.Mui-active, &.Mui-focusVisible": {
                            boxShadow: `0px 0px 0px 8px rgba(255, 193, 7, 0.16)`,
                          },
                          "&::before": { display: "none" },
                        },
                        "& .MuiSlider-rail": { opacity: 0.3, backgroundColor: "#D0D5DD" },
                      }}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#344054", mb: 1 }}>
                        Years
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        placeholder="0"
                        value={tenureYears === "" || tenureYears === 0 ? "" : tenureYears}
                        onChange={(event) => {
                          const val = event.target.value;
                          if (val === "" || val === null) {
                            setTenureYears("");
                          } else {
                            const parsed = Number(val);
                            setTenureYears(Number.isNaN(parsed) ? "" : parsed);
                          }
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            fontSize: 14,
                            fontWeight: 600,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#344054", mb: 1 }}>
                        Months
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        placeholder="0"
                        value={tenureMonths === "" || tenureMonths === 0 ? "" : tenureMonths}
                        onChange={(event) => {
                          const val = event.target.value;
                          if (val === "" || val === null) {
                            setTenureMonths("");
                          } else {
                            const parsed = Number(val);
                            setTenureMonths(Number.isNaN(parsed) ? "" : parsed);
                          }
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            fontSize: 14,
                            fontWeight: 600,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#344054", mb: 1 }}>
                        Schedule starts from
                      </Typography>
                      <TextField
                        fullWidth
                        type="month"
                        size="small"
                        value={startMonth}
                        onChange={(event) => setStartMonth(event.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            fontSize: 14,
                            fontWeight: 600,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {[
              {
                label: "Loan EMI",
                value: emi,
                background: "#EFF4FF",
                borderColor: Colors.PRIMARY || "#043b9f",
              },
              {
                label: "Total Interest Payable",
                value: totalInterest,
                background: "#FFFBEB",
                borderColor: Colors.SECONDARY || "#FFC107",
              },
              {
                label: "Total Payment",
                value: totalPayment,
                background: "#ECFDF5",
                borderColor: Colors.SUCCESS || "#029E3B",
              },
            ].map(({ label, value, background, borderColor }) => (
              <Grid item xs={12} md={4} key={label}>
                <Card
                  sx={{
                    height: "100%",
                    boxShadow:
                      "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)",
                    backgroundColor: background,
                    borderLeft: `5px solid ${borderColor}`,
                    borderRadius: "12px",
                    borderTop: "none",
                    borderRight: "none",
                    borderBottom: "none",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0px 12px 20px rgba(16, 24, 40, 0.08)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#475467",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 26,
                        fontWeight: 850,
                        color: "#101828",
                        mt: 1,
                      }}
                    >
                      {formatCurrency(value)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Card
                sx={{
                  maxWidth: 420,
                  mx: "auto",
                  boxShadow: "none",
                  border: "1px solid #D0D5DD",
                  backgroundColor: "#F5F5F5",
                }}
              >
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography
                    align="center"
                    sx={{ fontSize: 16, fontWeight: 700, color: "#1D2939", mb: 1.5 }}
                  >
                    Break-up of Total Payment
                  </Typography>
                  <Box sx={{ height: 280, width: "100%", mx: "auto" }}>
                    <Pie
                      ref={pieChartRef}
                      id="emi-pie-chart"
                      data={pieData}
                      plugins={[piePercentagePlugin]}
                      options={{
                        maintainAspectRatio: false,
                        responsive: true,
                        plugins: {
                          legend: {
                            position: "bottom",
                            labels: {
                              boxWidth: 12,
                              boxHeight: 12,
                              padding: 18,
                              color: "#101828",
                              font: { size: 12 },
                              usePointStyle: true,
                            },
                          },
                          tooltip: {
                            callbacks: {
                              label(context) {
                                return `${context.label}: ${formatCurrency(context.raw)}`;
                              },
                            },
                          },
                        },
                        animation: false,
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ height: 360, boxShadow: "none", border: "1px solid #EAECF0" }}>
                <CardContent sx={{ height: "100%" }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 2 }}>
                    Principal and Interest by Period
                  </Typography>
                  <Bar
                    ref={barChartRef}
                    id="emi-bar-chart"
                    data={barData}
                    options={{
                      maintainAspectRatio: false,
                      responsive: true,
                      animation: false,
                      scales: { x: { stacked: true }, y: { stacked: true } },
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ mt: 3, boxShadow: "none", border: "1px solid #EAECF0" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                    Schedule showing EMI payments
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#667085" }}>
                    Click a period row to view the monthly nested schedule.
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={groupBy}
                    onChange={(event, value) => value && setGroupBy(value)}
                    sx={{
                      border: "1px solid #D0D5DD",
                      borderRadius: "10px",
                      p: "3px",
                      backgroundColor: "#F9FAFB",
                      height: 40,
                      "& .MuiToggleButtonGroup-grouped": {
                        border: "none",
                        borderRadius: "8px !important",
                        px: 2,
                        fontSize: 13,
                        fontWeight: 600,
                        textTransform: "none",
                        color: "#475467",
                        transition: "all 0.2s ease",
                        "&.Mui-selected": {
                          backgroundColor: "#FFFFFF",
                          color: Colors.PRIMARY || "#043b9f",
                          boxShadow:
                            "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)",
                          border: "1px solid #EAECF0",
                          "&:hover": {
                            backgroundColor: "#FFFFFF",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "#F2F4F7",
                        },
                      },
                    }}
                  >
                    <ToggleButton value="calendar" disableRipple>
                      Calendar Year
                    </ToggleButton>
                    <ToggleButton value="financial" disableRipple>
                      Financial Year
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={downloadPdf}
                    sx={{
                      height: 40,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 14,
                      borderRadius: "10px",
                      borderColor: "#FDA29B",
                      color: "#B32318",
                      backgroundColor: "#FEF3F2",
                      "&:hover": {
                        backgroundColor: "#FEE4E2",
                        borderColor: "#FECDCA",
                      },
                      px: 2,
                    }}
                  >
                    Download PDF
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<TableChartIcon />}
                    onClick={downloadExcel}
                    sx={{
                      height: 40,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 14,
                      borderRadius: "10px",
                      borderColor: "#A6F4C5",
                      color: "#027A48",
                      backgroundColor: "#ECFDF3",
                      "&:hover": {
                        backgroundColor: "#D1FADF",
                        borderColor: "#6CE9A6",
                      },
                      px: 2,
                    }}
                  >
                    Download Excel
                  </Button>
                </Box>
              </Box>

              <Box sx={{ overflowX: "auto" }}>
                <Box sx={{ minWidth: 900 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "48px 18% repeat(4, 1fr)",
                      alignItems: "center",
                      backgroundColor: Colors.PRIMARY,
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 14,
                      minHeight: 56,
                      "& > div": { px: 2 },
                    }}
                  >
                    <Box />
                    <Box>Period</Box>
                    <Box textAlign="right">Principal</Box>
                    <Box textAlign="right">Interest</Box>
                    <Box textAlign="right">Total Payment</Box>
                    <Box textAlign="right">Balance</Box>
                  </Box>
                  {groupedSchedule.map((row) => (
                    <Box key={row.key}>
                      <Box
                        onClick={() =>
                          setExpandedRows((current) => ({
                            ...current,
                            [row.key]: !current[row.key],
                          }))
                        }
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "48px 18% repeat(4, 1fr)",
                          alignItems: "center",
                          minHeight: 56,
                          borderBottom: "1px solid #EAECF0",
                          cursor: "pointer",
                          backgroundColor: "#FFFFFF",
                          fontSize: 14,
                          color: "#1D2939",
                          "&:hover": { backgroundColor: "#F9FAFB" },
                          "& > div": { px: 2 },
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              border: "1px solid #D0D5DD",
                              borderRadius: "6px",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#101828",
                              backgroundColor: "#FFFFFF",
                            }}
                          >
                            {expandedRows[row.key] ? (
                              <KeyboardArrowDownIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowRightIcon fontSize="small" />
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ fontWeight: 600 }}>{row.key}</Box>
                        <Box textAlign="right">{formatCurrency(row.principal)}</Box>
                        <Box textAlign="right">{formatCurrency(row.interest)}</Box>
                        <Box textAlign="right">{formatCurrency(row.totalPayment)}</Box>
                        <Box textAlign="right">{formatCurrency(row.closingBalance)}</Box>
                      </Box>
                      <Collapse in={Boolean(expandedRows[row.key])} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            py: 1.5,
                            pl: 2,
                            pr: 2,
                            backgroundColor: "#F9FAFB",
                            borderLeft: `4px solid ${Colors.PRIMARY || "#043b9f"}`,
                            ml: "48px",
                            borderBottom: "1px solid #EAECF0",
                          }}
                        >
                          {/* Header of Nested Table */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "18% repeat(4, 1fr)",
                              alignItems: "center",
                              backgroundColor: "rgba(4, 59, 159, 0.05)",
                              minHeight: 38,
                              fontSize: 13,
                              fontWeight: 700,
                              color: Colors.PRIMARY || "#043b9f",
                              "& > div": { px: 2 },
                              borderRadius: "6px",
                              mb: 0.5,
                            }}
                          >
                            <Box>Month</Box>
                            <Box textAlign="right">Principal</Box>
                            <Box textAlign="right">Interest</Box>
                            <Box textAlign="right">Total Payment</Box>
                            <Box textAlign="right">Balance</Box>
                          </Box>

                          {/* Body of Nested Table */}
                          <Box>
                            {row.months.map((month) => (
                              <Box
                                key={`${row.key}-${month.monthNo}`}
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "18% repeat(4, 1fr)",
                                  alignItems: "center",
                                  minHeight: 36,
                                  fontSize: 13,
                                  borderBottom: "1px solid #F1F3F5",
                                  color: "#475467",
                                  "& > div": { px: 2 },
                                  "&:hover": {
                                    backgroundColor: "rgba(4, 59, 159, 0.02)",
                                    borderRadius: "4px",
                                  },
                                }}
                              >
                                <Box>{month.month}</Box>
                                <Box textAlign="right">{formatCurrency(month.principal)}</Box>
                                <Box textAlign="right">{formatCurrency(month.interest)}</Box>
                                <Box textAlign="right">{formatCurrency(month.emi)}</Box>
                                <Box textAlign="right">{formatCurrency(month.closingBalance)}</Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Typography sx={{ mt: 2, fontSize: 12, color: "#667085" }}>
            Principal: {numberFormatter.format(Number(loanAmount) || 0)} | Tenure: {totalMonths}{" "}
            months | Rate: {interestRate || 0}% p.a.
          </Typography>
        </Box>
      </Box>
    </DashboardLayout>
  );
}

export default EMICalculator;
